import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { createContext, lazy, useContext } from "react";
import { useAuth } from "./contexts/AuthContext";
import { useMaintenance } from "./contexts/MaintenanceContext";
import { usePermissions, NAV_PERMISSIONS } from "./contexts/PermissionsContext";

// Layout (keep eager — used on every page)
import { AppLayout } from "./components/layout/AppLayout";

// Auth pages (keep eager — first thing users see)
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
const UnauthorizedPage = lazy(() => import("./pages/UnauthorizedPage"));

// Lazy-load all other pages — only downloaded when user navigates to them
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const CustomersPage = lazy(() => import("./pages/CustomersPage"));
const CastingsPage = lazy(() => import("./pages/CastingsPage"));
const PatternsPage = lazy(() => import("./pages/PatternsPage"));
const GradesPage = lazy(() => import("./pages/GradesPage"));
const MaterialsPage = lazy(() => import("./pages/MaterialsPage"));
const MonthlyPlanningPage = lazy(() => import("./pages/MonthlyPlanningPage"));
const DailyPlanningPage = lazy(() => import("./pages/DailyPlanningPage"));
const HeatsPage = lazy(() => import("./pages/HeatsPage"));
const FettlingPage = lazy(() => import("./pages/FettlingPage"));
const PatternNumPage = lazy(() => import("./pages/PatternNumPage"));
const ReadyChequesPage = lazy(() => import("./pages/ReadyChequesPage"));
const MsedclReadingsPage = lazy(() => import("./pages/MsedclReadingsPage"));
const SandTestingReportPage = lazy(() => import("./pages/SandTestingReportPage"));
const ClosingStockPage = lazy(() => import("./pages/ClosingStockPage"));
const WorkOrderStatusPage = lazy(() => import("./pages/WorkOrderStatusPage"));
const QualityPage = lazy(() => import("./pages/QualityPage"));
const ExceReportPage = lazy(() => import("./pages/ExceReportPage"));
const MachiningPage = lazy(() => import("./pages/MachiningPage"));
const MachiningVendorsPage = lazy(() => import("./pages/MachiningVendorsPage"));
const PatternVendorsPage = lazy(() => import("./pages/PatternVendorsPage"));
const PatternContractsPage = lazy(() => import("./pages/PatternContractsPage"));
const ContractInvoicesPage = lazy(() => import("./pages/ContractInvoicesPage"));
const DispatchPage = lazy(() => import("./pages/DispatchPage"));
const ReportsHome = lazy(() => import("./pages/reports/ReportsHome"));
const MasterReportPage = lazy(() => import("./pages/reports/MasterReportPage"));
const CostingPage = lazy(() => import("./pages/reports/CostingPage"));
const HeatReportPage = lazy(() => import("./pages/reports/HeatReportPage"));
const SalesAnalysisPage = lazy(() => import("./pages/reports/SalesAnalysisPage"));
const WipReportPage = lazy(() => import("./pages/reports/WipReportPage"));
// The old Sales Orders report (pre-2026-10-28 wipe), restored 2026-11-05.
const SalesOrdersReportPage = lazy(() => import("./pages/reports/SalesOrdersReportPage"));
const ReportPrintPage = lazy(() => import("./pages/reports/ReportPrintPage"));
const DeveloperToolsPage = lazy(() => import("./pages/system/DeveloperToolsPage"));
const ImportPage = lazy(() => import("./pages/ImportPage"));
const ExportPage = lazy(() => import("./pages/ExportPage"));
const SalesPage = lazy(() => import("./pages/SalesPage"));
const PendingOrdersPage = lazy(() => import("./pages/PendingOrdersPage"));
const MarketingPage = lazy(() => import("./pages/MarketingPage"));
const MarketingLeadPage = lazy(() => import("./pages/MarketingLeadPage"));
const MarketingDiscoverPage = lazy(() => import("./pages/MarketingDiscoverPage"));
const MarketingStatusPage = lazy(() => import("./pages/MarketingStatusPage"));
const CustomerSalesPage = lazy(() => import("./pages/CustomerSalesPage"));
const CustomerSalesDetailPage = lazy(() => import("./pages/CustomerSalesDetailPage"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const TodoPage = lazy(() => import("./pages/TodoPage"));
const GlobalSearchPage = lazy(() => import("./pages/GlobalSearchPage"));
const NotesPage = lazy(() => import("./pages/NotesPage"));
const ToolsPage = lazy(() => import("./pages/tools/ToolsPage"));
const DocMatchPage = lazy(() => import("./pages/tools/docmatch/DocMatchPage"));
const SalesDashboardPage = lazy(() => import("./pages/SalesDashboardPage"));
const DocsHomePage = lazy(() => import("./pages/docs/DocsHomePage"));
const DocEditorPage = lazy(() => import("./pages/docs/DocEditorPage"));
const BnbHomePage = lazy(() => import("./pages/bnb/BnbHomePage"));
const BnbSheetEditorPage = lazy(() => import("./pages/bnb/BnbSheetEditorPage"));
const BanamobHomePage = lazy(() => import("./pages/banamob/BanamobHomePage"));
const BanamobFettlingPage = lazy(() => import("./pages/banamob/BanamobFettlingPage"));
const BanamobProsheetPage = lazy(() => import("./pages/banamob/BanamobProsheetPage"));

// Accounting Pages
const TallyCOPage = lazy(() => import("./pages/TallyCOPage"));
const DayBookPage = lazy(() => import("./pages/accounting/DayBookPage"));
const BillsPayablePage = lazy(() => import("./pages/accounting/BillsPayablePage"));
const BillsReceivablePage = lazy(() => import("./pages/accounting/BillsReceivablePage"));
const AdvancesPage = lazy(() => import("./pages/accounting/AdvancesPage"));
const SalesInvoicesPage = lazy(() => import("./pages/accounting/SalesInvoicesPage"));

// Purchase Pages
const VendorsPage = lazy(() => import("./pages/purchase/VendorsPage"));
const PurchaseOrdersPage = lazy(() => import("./pages/purchase/PurchaseOrdersPage"));
const PurchaseInvoicesPage = lazy(() => import("./pages/purchase/PurchaseInvoicesPage"));
const PurchaseChallansPage = lazy(() => import("./pages/purchase/PurchaseChallansPage"));
const VendorLedgerPage = lazy(() => import("./pages/purchase/VendorLedgerPage"));

// Stock Pages
const StockPage = lazy(() => import("./pages/stock/StockPage").then(m => ({ default: m.StockPage })));

// Store Pages
const StoreTransactionsPage = lazy(() => import("./pages/store/StoreTransactionsPage"));
const StoreConsumptionPage = lazy(() => import("./pages/store/StoreConsumptionPage"));
const MaterialRequestPage = lazy(() => import("./pages/store/MaterialRequest/MaterialRequestPage"));
const StoreHistoryPage = lazy(() => import("./pages/store/StoreHistoryPage"));

// Status/Tracking Pages
const StatusPage = lazy(() => import("./pages/StatusPage"));
const SalesOrderStatusPage = lazy(() => import("./pages/status/SalesOrderStatusPage"));
const OrderStatusPage = lazy(() => import("./pages/status/OrderStatusPage"));
const PatternOrderStatusPage = lazy(() => import("./pages/status/PatternOrderStatusPage"));
const MachiningOrderStatusPage = lazy(() => import("./pages/status/MachiningOrderStatusPage"));

// System Pages
const SystemStatusPage = lazy(() => import("./pages/SystemStatusPage"));
const BusinessProfilePage = lazy(() => import("./pages/system/BusinessProfilePage"));
const UsersPage = lazy(() => import("./pages/system/UsersPage"));
const UserPermissionsPage = lazy(() => import("./pages/system/UserPermissionsPage"));
const NotificationsPage = lazy(() => import("./pages/system/NotificationsPage"));
const ApprovalsPage = lazy(() => import("./pages/system/ApprovalsPage"));
const QuickAccessPage = lazy(() => import("./pages/system/QuickAccessPage"));
const DeveloperDashboard = lazy(() => import("./pages/system/DeveloperDashboard"));
const MaintenancePage = lazy(() => import("./pages/system/MaintenancePage"));
const MaintenanceLockPage = lazy(() => import("./pages/MaintenanceLockPage"));
const DashboardsManagementPage = lazy(() => import("./pages/system/DashboardsManagementPage"));
const DashboardBuilderPage = lazy(() => import("./pages/system/DashboardBuilderPage"));
const TermsConditionsPage = lazy(() => import("./pages/system/TermsConditionsPage"));
const ActivityPage = lazy(() => import("./pages/system/ActivityPage"));
const ActivityUsersPage = lazy(() => import("./pages/system/ActivityUsersPage"));
const ActivityUserProfilePage = lazy(() => import("./pages/system/ActivityUserProfilePage"));
const AISettingsPage = lazy(() => import("./pages/system/AISettingsPage"));
const SettingsPage = lazy(() => import("./pages/system/SettingsPage"));
const LLMTrainingPage = lazy(() => import("./pages/system/LLMTrainingPage"));
const FurnacesPage = lazy(() => import("./pages/system/FurnacesPage"));
const GetDataPage = lazy(() => import("./pages/system/GetDataPage"));
const DataManagementPage = lazy(() => import("./pages/system/DataManagementPage"));
const DuplicatesPage = lazy(() => import("./pages/system/DuplicatesPage"));

// Print Canvas Pages
const PrintCanvasPage = lazy(() => import("./pages/PrintCanvasPage"));
const CanvasBuilder = lazy(() => import("./components/print-canvas/CanvasBuilder"));
const UploadTemplate = lazy(() => import("./components/print-canvas/UploadTemplate"));
const PrintPreviewPage = lazy(() => import("./pages/PrintPreviewPage"));
const MonthlyPlanPrint = lazy(() => import("./components/print/MonthlyPlanPrint"));
const DailyPlanPrint = lazy(() => import("./components/print/DailyPlanPrint"));
const HeatEntryPrint = lazy(() => import("./components/print/HeatEntryPrint"));
const StoreTransactionsPrint = lazy(() => import("./components/print/StoreTransactionsPrint"));
const StoreHistoryPrint = lazy(() => import("./components/print/StoreHistoryPrint"));
const MachiningOrdersPrint = lazy(() => import("./components/print/MachiningOrdersPrint"));
const PTOOrderPrint = lazy(() => import("./components/print/PTOOrderPrint"));
const DispatchPrint = lazy(() => import("./components/print/DispatchPrint"));
const OrderStatusPrint = lazy(() => import("./components/print/OrderStatusPrint"));

// Loading spinner shown while lazy chunks download
export const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// True when this route tree renders inside a keep-alive tab frame. The app
// shell (sidebar/header/tab strip) is then rendered ONCE above the frames —
// see TabFrames — so ProtectedRoute must not wrap AppLayout again.
export const FrameEnvContext = createContext(false);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const { enabled: maintEnabled, isAllowed, loading: maintLoading } = useMaintenance();
  const { canAccessRoute } = usePermissions();
  const location = useLocation();
  const inFrame = useContext(FrameEnvContext);

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Block non-allowed users when maintenance is active
  if (!maintLoading && maintEnabled && !isAllowed) {
    return <Navigate to="/maintenance" replace />;
  }

  // Module/permission gate — matches NAV_PERMISSIONS which also drives the
  // sidebar's hidden links, so a hidden link and its direct URL agree.
  if (!canAccessRoute(location.pathname)) {
    const denied = (
      <UnauthorizedPage path={location.pathname} permission={NAV_PERMISSIONS[location.pathname]} />
    );
    return inFrame ? denied : <AppLayout>{denied}</AppLayout>;
  }

  if (inFrame) return children;

  return <AppLayout>{children}</AppLayout>;
};

// Standalone full-screen protected route (no AppLayout sidebar/header)
const ProtectedRouteStandalone = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const { enabled: maintEnabled, isAllowed, loading: maintLoading } = useMaintenance();
  const { canAccessRoute } = usePermissions();
  const location = useLocation();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!maintLoading && maintEnabled && !isAllowed) {
    return <Navigate to="/maintenance" replace />;
  }

  if (!canAccessRoute(location.pathname)) {
    return <UnauthorizedPage path={location.pathname} permission={NAV_PERMISSIONS[location.pathname]} />;
  }

  return children;
};

// Route for the maintenance lock page itself: requires login but never gets bounced.
// Auto-redirects to / if maintenance is off OR user is allowed (handled inside the page).
const MaintenanceRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// The full route tree. Rendered once per tab frame (kept alive per tab) and
// once "live" for tabless pages (login/docs/prints) — see TabFrames.
const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
    <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
    <Route path="/maintenance" element={<MaintenanceRoute><MaintenanceLockPage /></MaintenanceRoute>} />
    <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
    <Route path="/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
    <Route path="/marketing" element={<ProtectedRoute><MarketingPage mode="companies" /></ProtectedRoute>} />
    <Route path="/marketing/leads" element={<ProtectedRoute><MarketingPage mode="leads" /></ProtectedRoute>} />
    <Route path="/marketing/discover" element={<ProtectedRoute><MarketingDiscoverPage /></ProtectedRoute>} />
    <Route path="/marketing/status" element={<ProtectedRoute><MarketingStatusPage /></ProtectedRoute>} />
    <Route path="/marketing/customer-sales" element={<ProtectedRoute><CustomerSalesPage /></ProtectedRoute>} />
    <Route path="/marketing/customer-sales/:id" element={<ProtectedRoute><CustomerSalesDetailPage /></ProtectedRoute>} />
    <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
    {/* Personal TODO list — no permission gate: every user gets their own private
        list, and the server scopes it to their user id (routes/todos.js). */}
    <Route path="/todos" element={<ProtectedRoute><TodoPage /></ProtectedRoute>} />
    {/* Global search results — opened from the NowBar search box, not in the sidebar. */}
    <Route path="/search" element={<ProtectedRoute><GlobalSearchPage /></ProtectedRoute>} />
    <Route path="/notes" element={<ProtectedRoute><NotesPage /></ProtectedRoute>} />
    {/* Tools workspace (profile menu → More) — full-screen like BnB, own ToolsLayout,
        tabless. Ungated: tools are client-side utilities; DocMatch reads ERP Docs only
        through /api/docs, which applies its own access rules. */}
    <Route path="/tools" element={<ProtectedRouteStandalone><ToolsPage /></ProtectedRouteStandalone>} />
    <Route path="/tools/docmatch" element={<ProtectedRouteStandalone><DocMatchPage /></ProtectedRouteStandalone>} />
    <Route path="/docs" element={<ProtectedRouteStandalone><DocsHomePage /></ProtectedRouteStandalone>} />
    <Route path="/docs/:seriesId/:docId" element={<ProtectedRouteStandalone><DocEditorPage /></ProtectedRouteStandalone>} />
    <Route path="/bnb" element={<ProtectedRouteStandalone><BnbHomePage /></ProtectedRouteStandalone>} />
    <Route path="/bnb/sheets/:seriesId/:docId" element={<ProtectedRouteStandalone><BnbSheetEditorPage /></ProtectedRouteStandalone>} />
    <Route path="/banamob" element={<ProtectedRouteStandalone><BanamobHomePage /></ProtectedRouteStandalone>} />
    <Route path="/banamob/fettling" element={<ProtectedRouteStandalone><BanamobFettlingPage /></ProtectedRouteStandalone>} />
    <Route path="/banamob/prosheet/:docId" element={<ProtectedRouteStandalone><BanamobProsheetPage /></ProtectedRouteStandalone>} />
    <Route path="/marketing/:id" element={<ProtectedRoute><MarketingLeadPage /></ProtectedRoute>} />
    <Route path="/castings" element={<ProtectedRoute><CastingsPage /></ProtectedRoute>} />
    <Route path="/patterns" element={<ProtectedRoute><PatternsPage /></ProtectedRoute>} />
    <Route path="/grades" element={<ProtectedRoute><GradesPage /></ProtectedRoute>} />
    <Route path="/materials" element={<ProtectedRoute><MaterialsPage /></ProtectedRoute>} />
    <Route path="/monthly-planning" element={<ProtectedRoute><MonthlyPlanningPage /></ProtectedRoute>} />
    <Route path="/daily-planning" element={<ProtectedRoute><DailyPlanningPage /></ProtectedRoute>} />
    <Route path="/heats" element={<ProtectedRoute><HeatsPage /></ProtectedRoute>} />
    <Route path="/fettling" element={<ProtectedRoute><FettlingPage /></ProtectedRoute>} />
    <Route path="/sub-connect/pattern-num" element={<ProtectedRoute><PatternNumPage /></ProtectedRoute>} />
    <Route path="/sub-connect/ready-cheques" element={<ProtectedRoute><ReadyChequesPage /></ProtectedRoute>} />
    <Route path="/sub-connect/msedcl-readings" element={<ProtectedRoute><MsedclReadingsPage /></ProtectedRoute>} />
    <Route path="/sub-connect/sand-testing" element={<ProtectedRoute><SandTestingReportPage /></ProtectedRoute>} />
    <Route path="/sub-connect/closing-stock" element={<ProtectedRoute><ClosingStockPage /></ProtectedRoute>} />
    <Route path="/sub-connect/work-order-status" element={<ProtectedRoute><WorkOrderStatusPage /></ProtectedRoute>} />
    <Route path="/quality" element={<ProtectedRoute><QualityPage /></ProtectedRoute>} />
    <Route path="/exce-reports" element={<ProtectedRoute><ExceReportPage /></ProtectedRoute>} />
    {/* Sub Contract routes */}
    <Route path="/sub-contract/machining-contracts" element={<ProtectedRoute><MachiningPage /></ProtectedRoute>} />
    <Route path="/sub-contract/machining-vendors" element={<ProtectedRoute><MachiningVendorsPage /></ProtectedRoute>} />
    <Route path="/sub-contract/pattern-vendors" element={<ProtectedRoute><PatternVendorsPage /></ProtectedRoute>} />
    <Route path="/sub-contract/pattern-contracts" element={<ProtectedRoute><PatternContractsPage /></ProtectedRoute>} />
    <Route path="/sub-contract/contract-invoices" element={<ProtectedRoute><ContractInvoicesPage /></ProtectedRoute>} />
    {/* Redirects for old machining URLs */}
    <Route path="/machining" element={<Navigate to="/sub-contract/machining-contracts" replace />} />
    <Route path="/machining/vendors" element={<Navigate to="/sub-contract/machining-vendors" replace />} />
    <Route path="/dispatch" element={<ProtectedRoute><DispatchPage /></ProtectedRoute>} />
    {/* Reports rebuilt from scratch — any old report URL lands on the home page
        (except the old ?tab=sales-orders link, which ReportsHome forwards). */}
    <Route path="/reports" element={<ProtectedRoute><ReportsHome /></ProtectedRoute>} />
    <Route path="/reports/master" element={<ProtectedRoute><MasterReportPage /></ProtectedRoute>} />
    <Route path="/reports/costing" element={<ProtectedRoute><CostingPage /></ProtectedRoute>} />
    <Route path="/reports/heats" element={<ProtectedRoute><HeatReportPage /></ProtectedRoute>} />
    <Route path="/reports/sales" element={<ProtectedRoute><SalesAnalysisPage /></ProtectedRoute>} />
    <Route path="/reports/sales-orders" element={<ProtectedRoute><SalesOrdersReportPage /></ProtectedRoute>} />
    <Route path="/reports/wip" element={<ProtectedRoute><WipReportPage /></ProtectedRoute>} />
    <Route path="/reports/print" element={<ProtectedRoute><ReportPrintPage /></ProtectedRoute>} />
    <Route path="/reports/sync-health" element={<Navigate to="/system/developer-tools?tool=sync-health" replace />} />
    <Route path="/reports/*" element={<Navigate to="/reports" replace />} />
    <Route path="/system/developer-tools" element={<ProtectedRoute><DeveloperToolsPage /></ProtectedRoute>} />
    <Route path="/import" element={<ProtectedRoute><ImportPage /></ProtectedRoute>} />
    <Route path="/export" element={<ProtectedRoute><ExportPage /></ProtectedRoute>} />
    <Route path="/sales" element={<ProtectedRoute><SalesPage /></ProtectedRoute>} />
    <Route path="/sales/pending-orders" element={<ProtectedRoute><PendingOrdersPage /></ProtectedRoute>} />
    <Route path="/sales-dashboard" element={<ProtectedRoute><SalesDashboardPage /></ProtectedRoute>} />

    {/* Accounting Routes */}
    <Route path="/tally-co" element={<ProtectedRoute><TallyCOPage /></ProtectedRoute>} />
    <Route path="/accounting/day-book" element={<ProtectedRoute><DayBookPage /></ProtectedRoute>} />
    <Route path="/accounting/bills-payable" element={<ProtectedRoute><BillsPayablePage /></ProtectedRoute>} />
    <Route path="/accounting/bills-receivable" element={<ProtectedRoute><BillsReceivablePage /></ProtectedRoute>} />
    <Route path="/accounting/advances" element={<ProtectedRoute><AdvancesPage /></ProtectedRoute>} />
    {/* Sales Invoices is linked from the Sales menu, not Accounting. */}
    <Route path="/accounting/sales-invoices" element={<ProtectedRoute><SalesInvoicesPage /></ProtectedRoute>} />
    {/* Accounting trimmed to Day Book / Receivable / Payable / TallyCO (2026-10-22).
        Removed pages redirect so old bookmarks and open tabs don't 404. */}
    <Route path="/purchase/data" element={<Navigate to="/accounting/day-book" replace />} />
    <Route path="/accounting/sales-data" element={<Navigate to="/accounting/day-book" replace />} />
    <Route path="/accounting/bank-accounts" element={<Navigate to="/accounting/day-book" replace />} />
    <Route path="/accounting/balance-sheet/*" element={<Navigate to="/accounting/day-book" replace />} />
    <Route path="/accounting/chart-of-accounts/*" element={<Navigate to="/accounting/day-book" replace />} />

    {/* Purchase Routes */}
    <Route path="/purchase/vendors" element={<ProtectedRoute><VendorsPage /></ProtectedRoute>} />
    <Route path="/purchase/orders" element={<ProtectedRoute><PurchaseOrdersPage /></ProtectedRoute>} />
    <Route path="/purchase/challans" element={<ProtectedRoute><PurchaseChallansPage /></ProtectedRoute>} />
    <Route path="/purchase/invoices" element={<ProtectedRoute><PurchaseInvoicesPage /></ProtectedRoute>} />
    {/* Tally Invoices was merged into Purchase Invoices — it's now the whole page
        (2026-09-02, ERP manual invoice entry removed entirely). Redirect old bookmarks/links. */}
    <Route path="/purchase/tally-invoices" element={<Navigate to="/purchase/invoices" replace />} />
    <Route path="/purchase/vendors/:vendorId/ledger" element={<ProtectedRoute><VendorLedgerPage /></ProtectedRoute>} />

    {/* Stock Routes */}
    <Route path="/stock" element={<ProtectedRoute><StockPage /></ProtectedRoute>} />

    {/* Store Routes */}
    <Route path="/store" element={<ProtectedRoute><StoreTransactionsPage /></ProtectedRoute>} />
    <Route path="/store/consumption" element={<ProtectedRoute><StoreConsumptionPage /></ProtectedRoute>} />
    <Route path="/store/requests" element={<ProtectedRoute><MaterialRequestPage /></ProtectedRoute>} />
    <Route path="/store/history" element={<ProtectedRoute><StoreHistoryPage /></ProtectedRoute>} />

    {/* Status/Tracking Routes */}
    <Route path="/status" element={<ProtectedRoute><StatusPage /></ProtectedRoute>} />
    <Route path="/status/sales-orders" element={<ProtectedRoute><SalesOrderStatusPage /></ProtectedRoute>} />
    {/* One order; Sales users reach it too, so no NAV_PERMISSIONS entry — /tracking takes view_status or view_sales */}
    <Route path="/status/sales-orders/:ref" element={<ProtectedRoute><OrderStatusPage /></ProtectedRoute>} />
    <Route path="/status/pattern-orders" element={<ProtectedRoute><PatternOrderStatusPage /></ProtectedRoute>} />
    <Route path="/status/machining-orders" element={<ProtectedRoute><MachiningOrderStatusPage /></ProtectedRoute>} />

    {/* System Routes */}
    <Route path="/system/business-profile" element={<ProtectedRoute><BusinessProfilePage /></ProtectedRoute>} />
    <Route path="/system/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
    <Route path="/system/users/:userId/permissions" element={<ProtectedRoute><UserPermissionsPage /></ProtectedRoute>} />
    <Route path="/system/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
    <Route path="/system/approvals" element={<ProtectedRoute><ApprovalsPage /></ProtectedRoute>} />
    <Route path="/system/approvals/:tab" element={<ProtectedRoute><ApprovalsPage /></ProtectedRoute>} />
    <Route path="/system/quick-access" element={<ProtectedRoute><QuickAccessPage /></ProtectedRoute>} />
    <Route path="/system/ai-settings" element={<ProtectedRoute><AISettingsPage /></ProtectedRoute>} />
    <Route path="/system/llm" element={<ProtectedRoute><LLMTrainingPage /></ProtectedRoute>} />
    <Route path="/system/developer" element={<ProtectedRoute><DeveloperDashboard /></ProtectedRoute>} />
    <Route path="/system/maintenance" element={<ProtectedRoute><MaintenancePage /></ProtectedRoute>} />
    <Route path="/system/dashboards" element={<ProtectedRoute><DashboardsManagementPage /></ProtectedRoute>} />
    <Route path="/system/dashboards/:id/builder" element={<ProtectedRouteStandalone><DashboardBuilderPage /></ProtectedRouteStandalone>} />
    <Route path="/system/furnaces" element={<ProtectedRoute><FurnacesPage /></ProtectedRoute>} />
    <Route path="/system/terms-conditions" element={<ProtectedRoute><TermsConditionsPage /></ProtectedRoute>} />
    <Route path="/system/get-data" element={<ProtectedRoute><GetDataPage /></ProtectedRoute>} />
    <Route path="/system/activity" element={<ProtectedRoute><ActivityPage /></ProtectedRoute>} />
    <Route path="/system/activity/users" element={<ProtectedRoute><ActivityUsersPage /></ProtectedRoute>} />
    <Route path="/system/activity/users/:id" element={<ProtectedRoute><ActivityUserProfilePage /></ProtectedRoute>} />
    <Route path="/system/data" element={<ProtectedRoute><DataManagementPage /></ProtectedRoute>} />
    <Route path="/system/duplicates" element={<ProtectedRoute><DuplicatesPage /></ProtectedRoute>} />
    <Route path="/system/status" element={<ProtectedRoute><SystemStatusPage /></ProtectedRoute>} />
    <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

    {/* Print Canvas Routes */}
    <Route path="/print-canvas" element={<ProtectedRoute><PrintCanvasPage /></ProtectedRoute>} />
    <Route path="/print-canvas/canvas" element={<ProtectedRoute><CanvasBuilder /></ProtectedRoute>} />
    <Route path="/print-canvas/upload" element={<ProtectedRoute><UploadTemplate /></ProtectedRoute>} />
    <Route path="/print-preview" element={<ProtectedRoute><PrintPreviewPage /></ProtectedRoute>} />
    <Route path="/print/monthly-plan" element={<ProtectedRouteStandalone><MonthlyPlanPrint /></ProtectedRouteStandalone>} />
    <Route path="/print/daily-plan" element={<ProtectedRouteStandalone><DailyPlanPrint /></ProtectedRouteStandalone>} />
    <Route path="/print/heat-entry" element={<ProtectedRouteStandalone><HeatEntryPrint /></ProtectedRouteStandalone>} />
    <Route path="/print/store-transactions" element={<ProtectedRouteStandalone><StoreTransactionsPrint /></ProtectedRouteStandalone>} />
    <Route path="/print/store-history" element={<ProtectedRouteStandalone><StoreHistoryPrint /></ProtectedRouteStandalone>} />
    <Route path="/print/machining-order" element={<ProtectedRouteStandalone><MachiningOrdersPrint /></ProtectedRouteStandalone>} />
    <Route path="/print/pattern-order" element={<ProtectedRouteStandalone><PTOOrderPrint /></ProtectedRouteStandalone>} />
    <Route path="/print/dispatch" element={<ProtectedRouteStandalone><DispatchPrint /></ProtectedRouteStandalone>} />
    <Route path="/print/order-status" element={<ProtectedRouteStandalone><OrderStatusPrint /></ProtectedRouteStandalone>} />
  </Routes>
);

export default AppRoutes;
